import { useMemo } from "react";
import { Alert, Fieldset, Stack, Text, Textarea } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type {
  LegacyReportDraftData,
  LegacyReportManifest,
} from "../../../types/legacyReportReview";

interface Props {
  form: UseFormReturnType<LegacyReportDraftData>;
  manifest: LegacyReportManifest;
  readOnly?: boolean;
}

export default function LegacyReportSourceReview({
  form,
  manifest,
  readOnly = false,
}: Props) {
  const resolutions = useMemo(
    () =>
      form.values.warningResolutions.map((resolution, index) => ({
        ...resolution,
        index,
        warning: manifest.warnings.find(
          (warning) => warning.id === resolution.warningId
        ),
      })),
    [form.values.warningResolutions, manifest.warnings]
  );
  return (
    <Stack gap="md">
      <Fieldset legend="Historical signatures and attribution">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Preserved attribution belongs to the original historical content.
            Approval records today's fidelity review and does not create a
            historical clinical signature.
          </Text>
          {form.values.signatures.length ? (
            form.values.signatures.map((signature, index) => (
              <Stack key={signature.id} gap="xs">
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                  Source attribution:{" "}
                  {manifest.signatures.find(
                    (source) => source.id === signature.id
                  )?.text || "Review the original source page."}
                </Text>
                <Textarea
                  readOnly={readOnly}
                  label={`Printed attribution ${index + 1}${
                    signature.sourcePage
                      ? ` · source page ${signature.sourcePage}`
                      : ""
                  }`}
                  autosize
                  minRows={2}
                  {...form.getInputProps(`signatures.${index}.text`)}
                />
              </Stack>
            ))
          ) : (
            <Text size="sm">
              No signature text was extracted. Check the original and any
              flagged source regions.
            </Text>
          )}
        </Stack>
      </Fieldset>
      {manifest.warnings.length > 0 && (
        <Fieldset legend="Source review findings">
          <Stack gap="md">
            <Alert color="orange">
              Resolve each source warning against the original. Record what you
              checked; clinical facts must remain literal.
            </Alert>
            {resolutions.map(({ warningId, index, warning }) => (
              <Textarea
                key={warningId}
                readOnly={readOnly}
                label={warning?.message || warningId}
                description={
                  warning?.pageNumber
                    ? `Source page ${warning.pageNumber}`
                    : undefined
                }
                autosize
                minRows={2}
                placeholder="Describe the source evidence and how this concern was resolved"
                {...form.getInputProps(`warningResolutions.${index}.note`)}
              />
            ))}
          </Stack>
        </Fieldset>
      )}
    </Stack>
  );
}
